// Batch-11 KL2 — interne STREEP-beoordeling van een monteur (HR/teamleider).
//
// STRIKT GESCHEIDEN van de klant-beoordeling. Eén gedeeld schema voor API +
// form. De klant ziet deze dataset nooit; alles loopt via de interne backoffice.

import { z } from 'zod'

export const INTERNE_BEOORDELING_TYPES = [
  'ALGEMEEN',
  'FUNCTIONERINGSGESPREK',
  'KLANTFEEDBACK_SCREENING',
] as const
export type InterneBeoordelingType = (typeof INTERNE_BEOORDELING_TYPES)[number]

export const INTERNE_BEOORDELING_TYPE_LABELS: Record<InterneBeoordelingType, string> = {
  ALGEMEEN: 'Algemene beoordeling',
  FUNCTIONERINGSGESPREK: 'Functioneringsgesprek',
  KLANTFEEDBACK_SCREENING: 'Klantfeedback-screening',
}

export const INTERNE_BEOORDELING_STATUSES = ['CONCEPT', 'DEFINITIEF'] as const
export type InterneBeoordelingStatus = (typeof INTERNE_BEOORDELING_STATUSES)[number]

export const INTERNE_BEOORDELING_STATUS_LABELS: Record<InterneBeoordelingStatus, string> = {
  CONCEPT: 'Concept',
  DEFINITIEF: 'Definitief',
}

// ─── Score-rij (aspect + 1-5) ────────────────────────────────────────────────

export const InterneBeoordelingScoreSchema = z.object({
  aspect: z.string().trim().min(1, 'Geef het aspect op').max(120),
  score: z.number().int().min(1, 'Geef minimaal 1').max(5, 'Maximaal 5'),
  toelichting: z.string().trim().max(2000).optional(),
})
export type InterneBeoordelingScoreInput = z.infer<typeof InterneBeoordelingScoreSchema>

// ─── Create-schema ───────────────────────────────────────────────────────────

export const CreateInterneBeoordelingSchema = z.object({
  employeeId: z.string().min(1, 'Kies een medewerker'),
  type: z.enum(INTERNE_BEOORDELING_TYPES),
  periodeLabel: z.string().trim().min(1, 'Geef een periode op').max(60),
  // Totaalscore optioneel (alleen narratief mag ook).
  score: z.number().int().min(1).max(5).nullable().optional(),
  samenvatting: z.string().trim().max(5000).nullable().optional(),
  // Optionele koppelingen (referentie/HRM).
  gekoppeldeClientBeoordelingId: z.string().min(1).nullable().optional(),
  skillId: z.string().min(1).nullable().optional(),
  scores: z.array(InterneBeoordelingScoreSchema).max(30).optional(),
})
export type CreateInterneBeoordelingInput = z.infer<typeof CreateInterneBeoordelingSchema>

// Vaststellen (CONCEPT -> DEFINITIEF) en delen met de monteur.
export const UpdateInterneBeoordelingStatusSchema = z.object({
  status: z.enum(INTERNE_BEOORDELING_STATUSES).optional(),
  gedeeldMetMonteur: z.boolean().optional(),
})
export type UpdateInterneBeoordelingStatusInput = z.infer<
  typeof UpdateInterneBeoordelingStatusSchema
>

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface InterneBeoordelingScoreDTO {
  id: string
  aspect: string
  score: number
  toelichting: string | null
}

export interface InterneBeoordelingDTO {
  id: string
  employeeId: string
  employeeNaam: string | null
  beoordelaarUserId: string
  beoordelaarNaam: string | null
  type: InterneBeoordelingType
  status: InterneBeoordelingStatus
  periodeLabel: string
  score: number | null
  samenvatting: string | null
  gekoppeldeClientBeoordelingId: string | null
  skillId: string | null
  skillNaam: string | null
  gedeeldMetMonteur: boolean
  gedeeldOp: string | null
  createdAt: string
  updatedAt: string
  scores: InterneBeoordelingScoreDTO[]
}
